import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Global from '../../../../../store/global-selectors';
import {
  selectCustomsDocumentsCarrierCapabilities,
  selectCustomsDocumentsIsLoadingCapabilities,
  selectCustomsDocumentsCapabilitiesError,
  selectCustomsDocumentsOrderDocuments,
  selectCustomsDocumentsIsSendingShipmentDocuments,
  selectCustomsDocumentsShipmentHasSubmittedDocuments,
  selectCustomsDocumentsShipmentDocuments,
  selectCustomsDocumentsCarrierSupportsDocumentUpload,
  selectCustomsDocumentsCarrierIdForUpload,
} from '../../../../../store/customs-documents/customs-documents.selectors';
import {
  getCarrierDocumentCapabilitiesRequest,
  getOrderDocumentsRequest,
  getShipmentDocumentsRequest,
  sendShipmentDocumentsRequest,
} from '../../../../../store/customs-documents/customs-documents.actions';
import { getDocumentTypesForCarrier } from './document-type-dropdown';

type UseCustomsDocumentsParams = {
  salesOrderId?: string | null;
  shipmentId?: string | null;
  fulfillmentPlanId?: string | null;
};

/**
 * Encapsulates all data fetching and state selection for customs document upload.
 *
 * Pass `salesOrderId` for pre-ship (order-level documents).
 * Pass `shipmentId` for post-ship (shipment-level documents).
 * Both can be passed simultaneously (e.g. CustomsDocumentsSection handles both phases).
 * Pass `fulfillmentPlanId` to filter document types by the carrier assigned to the plan.
 *
 * Note: `isCarrierSupported` is informational only -- per spec, the upload UI should
 * still be rendered even when the carrier does not support it. Show a warning badge instead.
 */
export const useCustomsDocuments = ({
  salesOrderId: salesOrderIdProp = null,
  shipmentId = null,
  fulfillmentPlanId = null,
}: UseCustomsDocumentsParams = {}) => {
  const dispatch = useDispatch();

  // Resolve salesOrderId: use prop if provided, otherwise derive from fulfillmentPlanId
  const salesOrderId = useSelector((state) => {
    if (salesOrderIdProp) return salesOrderIdProp;
    if (!fulfillmentPlanId) return null;
    const salesOrder = Global.selectEntitiesSalesOrdersByFulfillmentPlanIds(state, { fulfillmentPlanIds: [fulfillmentPlanId] })?.[0];
    return salesOrder?.salesOrderId ?? null;
  });

  const capabilities = useSelector((state) => selectCustomsDocumentsCarrierCapabilities(state));
  const isLoadingCapabilities = useSelector((state) => selectCustomsDocumentsIsLoadingCapabilities(state));
  const capabilitiesError = useSelector((state) => selectCustomsDocumentsCapabilitiesError(state));

  const orderDocuments = useSelector((state) => selectCustomsDocumentsOrderDocuments(state, salesOrderId));
  const isSending = useSelector((state) => selectCustomsDocumentsIsSendingShipmentDocuments(state, shipmentId));
  const hasSubmittedDocs = useSelector((state) => selectCustomsDocumentsShipmentHasSubmittedDocuments(state, shipmentId));
  const shipmentDocuments = useSelector((state) => selectCustomsDocumentsShipmentDocuments(state, shipmentId));

  // Resolve carrier from fulfillment plan → seller service → carrierApiCode
  // Uses seller.data.services which is always loaded (no CORS issues on localhost).
  const serviceId = useSelector((state) =>
    fulfillmentPlanId ? Global.selectEntitiesFulfillmentPlanServiceId(state, { fulfillmentPlanId }) : null,
  );
  const carrierApiCode = useSelector((state) => {
    if (!serviceId) return null;
    const service = Global.selectSellerServiceById(state, { serviceId });
    return service?.carrierApiCode ?? null;
  });

  // Resolve tracking number from fulfillment entity (needed for shipment document upload API)
  const trackingNumber = useSelector((state) => {
    if (!shipmentId) return null;
    const fulfillment = Global.selectEntitiesFulfillmentById(state, { fulfillmentId: shipmentId });
    return fulfillment?.trackingInformation?.trackingNumber ?? null;
  });

  useEffect(() => {
    if (!capabilities && !isLoadingCapabilities && !capabilitiesError) {
      dispatch(getCarrierDocumentCapabilitiesRequest());
    }
  }, [dispatch, capabilities, isLoadingCapabilities, capabilitiesError]);

  useEffect(() => {
    if (salesOrderId) {
      dispatch(getOrderDocumentsRequest(salesOrderId));
    }
  }, [dispatch, salesOrderId]);

  useEffect(() => {
    if (shipmentId) {
      dispatch(getShipmentDocumentsRequest(shipmentId));
    }
  }, [dispatch, shipmentId]);

  const handleRefresh = useCallback(() => {
    if (salesOrderId) dispatch(getOrderDocumentsRequest(salesOrderId));
    if (shipmentId) dispatch(getShipmentDocumentsRequest(shipmentId));
  }, [dispatch, salesOrderId, shipmentId]);

  const handleSendToCarrier = useCallback(() => {
    if (shipmentId) {
      dispatch(sendShipmentDocumentsRequest(shipmentId));
    }
  }, [dispatch, shipmentId]);

  const isCarrierSupported = useSelector((state) =>
    carrierApiCode ? selectCustomsDocumentsCarrierSupportsDocumentUpload(state, carrierApiCode) : false,
  );
  // Base carrier ID for API payloads (e.g. "fedex" instead of "fedex_international_economy")
  const carrierCodeForUpload = useSelector((state) =>
    carrierApiCode ? selectCustomsDocumentsCarrierIdForUpload(state, carrierApiCode) : null,
  );
  const availableDocumentTypes = useMemo(() => {
    const allTypes = capabilities?.document_types ?? capabilities?.documentTypes ?? [];
    return getDocumentTypesForCarrier(carrierApiCode, allTypes);
  }, [carrierApiCode, capabilities]);
  const hasUploadedDocs = shipmentDocuments.some((doc: { status: string }) => doc.status === 'uploaded');

  return {
    isLoadingCapabilities,
    isCarrierSupported,
    availableDocumentTypes,
    carrierApiCode,
    carrierCodeForUpload,
    trackingNumber,
    salesOrderId,
    orderDocuments,
    isSending,
    hasSubmittedDocs,
    hasUploadedDocs,
    shipmentDocuments,
    handleRefresh,
    handleSendToCarrier,
  };
};
